import type { InitialState } from '@/app';
import CurrentPasswordUpdateForm from '@/components/CurrentPasswordUpdateForm';
import CurrentUserProfileUpdateForm from '@/components/CurrentUserProfileUpdateForm';
import CurrentUserProfileView from '@/components/CurrentUserProfileView';
import { useAuthToken } from '@/hooks/auth/useAuthToken';
import { useSiderCollapse } from '@/hooks/useSiderCollapse';
import { logout } from '@/services/auth/logoutService';
import type { User } from '@/types/user';
import { reloginAfterPasswordUpdate } from '@/utils/auth/reloginAfterPasswordUpdate';
import { normalizeImageSource } from '@/utils/imageSource';
import { logger } from '@/utils/logger';
import {
  IdcardOutlined,
  LockOutlined,
  LogoutOutlined,
  ProfileOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { history, useModel } from '@umijs/max';
import {
  Avatar,
  Button,
  Dropdown,
  message,
  Modal,
  Space,
  Typography,
  type AvatarProps,
  type MenuProps,
} from 'antd';
import React, { useCallback, useEffect, useState } from 'react';

const PROFILE_MODAL_WIDTH = 'min(820px, calc(100vw - 24px))';
const PHOTO_EDITOR_MODAL_WIDTH = 'min(560px, calc(100vw - 24px))';
const PROFILE_VIEW_MODAL_WIDTH = 'min(720px, calc(100vw - 24px))';

type UserMenuFooterMode = 'desktop' | 'mobile';

type UserMenuFooterProps = {
  mode?: UserMenuFooterMode;
};

const UserMenuFooter: React.FC<UserMenuFooterProps> = ({
  mode = 'desktop',
}) => {
  const isMobileMode = mode === 'mobile';
  const { currentUser, fetchCurrentUser, clearCurrentUser, setCurrentUser } =
    useModel('user');
  const { setInitialState } = useModel('@@initialState');
  const { setAuthTokens, removeAuthTokens } = useAuthToken();
  const { collapsed } = useSiderCollapse();
  const [isPasswordModalOpen, setPasswordModalOpen] = useState(false);
  const [isProfileModalOpen, setProfileModalOpen] = useState(false);
  const [isProfileViewModalOpen, setProfileViewModalOpen] = useState(false);
  const [avatarImageError, setAvatarImageError] = useState(false);

  const profile = currentUser?.profile;
  const displayName =
    (currentUser &&
      ([profile?.first_name, profile?.last_name].filter(Boolean).join(' ') ||
        currentUser.email)) ||
    undefined;

  const avatarProps: AvatarProps = {};
  const normalizedThumbnail = normalizeImageSource(profile?.thumbnail);
  const normalizedPhoto = normalizeImageSource(profile?.photo);
  const avatarSrc = avatarImageError
    ? undefined
    : normalizedThumbnail || normalizedPhoto || undefined;

  useEffect(() => {
    setAvatarImageError(false);
  }, [normalizedPhoto, normalizedThumbnail]);

  if (avatarSrc) {
    avatarProps.src = avatarSrc;
    avatarProps.onError = () => {
      setAvatarImageError(true);
      return false;
    };
  } else {
    avatarProps.icon = <UserOutlined />;
    avatarProps.size = 'small';
  }

  const handleConfirmLogout = useCallback(async () => {
    try {
      await logout();
    } catch (error) {
      logger.error('Logout error', {
        scope: 'user_menu_footer',
        error,
      });
    }
    removeAuthTokens();
    clearCurrentUser();
    setInitialState?.((prev?: InitialState) => ({
      ...prev,
      currentUser: undefined,
    }));
    history.replace('/login');
  }, [removeAuthTokens, clearCurrentUser, setInitialState]);

  const clearSessionAndRedirectToLogin = useCallback(() => {
    removeAuthTokens();
    clearCurrentUser();
    setInitialState?.((prev?: InitialState) => ({
      ...prev,
      currentUser: undefined,
    }));
    history.replace('/login');
  }, [removeAuthTokens, clearCurrentUser, setInitialState]);

  const syncCurrentUser = useCallback(
    (nextUser?: User) => {
      setCurrentUser(nextUser);
      setInitialState?.((prev?: InitialState) => ({
        ...prev,
        currentUser: nextUser,
      }));
    },
    [setCurrentUser, setInitialState],
  );

  const handleLogoutClick = useCallback(() => {
    Modal.confirm({
      title: 'Cerrar sesión',
      content: '¿Seguro que deseás cerrar sesión?',
      okText: 'Cerrar sesión',
      cancelText: 'Cancelar',
      centered: true,
      onOk: handleConfirmLogout,
    });
  }, [handleConfirmLogout]);

  const handlePasswordModalClose = useCallback(() => {
    setPasswordModalOpen(false);
  }, []);

  const handleProfileModalClose = useCallback(() => {
    setProfileModalOpen(false);
  }, []);

  const handleOpenPasswordFromProfile = useCallback(() => {
    setProfileModalOpen(false);
    setPasswordModalOpen(true);
  }, []);

  const handleProfileViewModalClose = useCallback(() => {
    setProfileViewModalOpen(false);
  }, []);

  const handleOpenProfileEditor = useCallback(() => {
    setProfileViewModalOpen(false);
    setProfileModalOpen(true);
  }, []);

  const handleProfileUpdateSuccess = useCallback(async () => {
    const refreshedUser = await fetchCurrentUser();
    if (refreshedUser) {
      setInitialState?.((prev?: InitialState) => ({
        ...prev,
        currentUser: refreshedUser,
      }));
    }
    setProfileModalOpen(false);
  }, [fetchCurrentUser, setInitialState]);

  const handlePasswordUpdateSuccess = useCallback(
    async ({ password }: { password: string }) => {
      const email = currentUser?.email;

      if (!email) {
        message.error(
          'La contraseña se actualizó, pero debés volver a ingresar con tu nueva contraseña.',
        );
        setPasswordModalOpen(false);
        clearSessionAndRedirectToLogin();
        return;
      }

      try {
        await reloginAfterPasswordUpdate({
          email,
          password,
          currentUser,
          setAuthTokens,
          syncCurrentUser,
          fetchCurrentUser,
        });
        setPasswordModalOpen(false);
      } catch (error) {
        logger.error('Automatic re-login after password update failed', {
          scope: 'user_menu_footer',
          error,
        });
        syncCurrentUser({
          ...currentUser,
          update_password_required: false,
        });
        setPasswordModalOpen(false);
      }
    },
    [
      currentUser,
      setAuthTokens,
      syncCurrentUser,
      fetchCurrentUser,
      clearSessionAndRedirectToLogin,
    ],
  );

  const handleUserAction = useCallback(
    (key: string) => {
      if (key === 'view-profile') {
        setProfileViewModalOpen(true);
        return;
      }
      if (key === 'update-profile') {
        setProfileModalOpen(true);
        return;
      }
      if (key === 'change-password') {
        setPasswordModalOpen(true);
        return;
      }
      if (key === 'logout') {
        handleLogoutClick();
      }
    },
    [handleLogoutClick],
  );

  const handleUserMenuClick = useCallback<NonNullable<MenuProps['onClick']>>(
    ({ key }) => {
      handleUserAction(String(key));
    },
    [handleUserAction],
  );

  const userMenuItems: Array<{
    key: string;
    icon: React.ReactNode;
    label: string;
  }> = [
    {
      key: 'view-profile',
      icon: <IdcardOutlined />,
      label: 'Ver perfil',
    },
    {
      key: 'update-profile',
      icon: <ProfileOutlined />,
      label: 'Actualizar perfil',
    },
    {
      key: 'change-password',
      icon: <LockOutlined />,
      label: 'Actualizar contraseña',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Cerrar sesión',
    },
  ];

  const profileModals = (
    <>
      <Modal
        centered
        destroyOnHidden
        title="Mi perfil"
        open={isProfileViewModalOpen}
        width={PROFILE_VIEW_MODAL_WIDTH}
        onCancel={handleProfileViewModalClose}
        okText="Editar perfil"
        cancelText="Cerrar"
        onOk={handleOpenProfileEditor}
      >
        <CurrentUserProfileView currentUser={currentUser} />
      </Modal>
      <Modal
        centered
        destroyOnHidden
        title="Actualizar perfil"
        open={isProfileModalOpen}
        width={PROFILE_MODAL_WIDTH}
        onCancel={handleProfileModalClose}
        footer={null}
      >
        <CurrentUserProfileUpdateForm
          currentUser={currentUser}
          photoModalWidth={PHOTO_EDITOR_MODAL_WIDTH}
          showCancelButton
          onCancel={handleProfileModalClose}
          onSuccess={handleProfileUpdateSuccess}
          onOpenPasswordUpdate={handleOpenPasswordFromProfile}
        />
      </Modal>
      <Modal
        centered
        destroyOnHidden
        title="Actualizar contraseña"
        open={isPasswordModalOpen}
        width="min(480px, calc(100vw - 24px))"
        onCancel={handlePasswordModalClose}
        footer={null}
      >
        <CurrentPasswordUpdateForm
          showCancelButton
          onCancel={handlePasswordModalClose}
          onSuccess={handlePasswordUpdateSuccess}
        />
      </Modal>
    </>
  );

  if (!currentUser) {
    if (isMobileMode) {
      return (
        <>
          <div
            style={{
              width: '100%',
              paddingTop: 12,
              borderTop: '1px solid rgba(5, 5, 5, 0.06)',
            }}
          >
            <Button
              type="text"
              block
              icon={<LogoutOutlined />}
              style={{ justifyContent: 'flex-start' }}
              onClick={handleLogoutClick}
            >
              Cerrar sesión
            </Button>
          </div>
          {profileModals}
        </>
      );
    }

    return (
      <>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
          }}
        >
          <Button
            type="text"
            block={!collapsed}
            icon={<LogoutOutlined />}
            style={{
              justifyContent: collapsed ? 'center' : 'flex-start',
              paddingLeft: collapsed ? 0 : undefined,
            }}
            onClick={handleLogoutClick}
          >
            {!collapsed && 'Cerrar sesión'}
          </Button>
        </div>
        {profileModals}
      </>
    );
  }

  if (isMobileMode) {
    return (
      <>
        <div
          style={{
            width: '100%',
            paddingTop: 12,
            borderTop: '1px solid rgba(5, 5, 5, 0.06)',
          }}
        >
          <Space
            align="center"
            size={8}
            style={{
              width: '100%',
              paddingInline: 4,
              marginBottom: 8,
            }}
          >
            <Avatar {...avatarProps} />
            <Typography.Text
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {displayName ?? 'Mi cuenta'}
            </Typography.Text>
          </Space>
          {userMenuItems.map((item) => (
            <Button
              key={item.key}
              type="text"
              block
              icon={item.icon}
              style={{ justifyContent: 'flex-start' }}
              onClick={() => handleUserAction(item.key)}
            >
              {item.label}
            </Button>
          ))}
        </div>
        {profileModals}
      </>
    );
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
        }}
      >
        <Dropdown
          trigger={['click']}
          menu={{
            items: userMenuItems,
            onClick: handleUserMenuClick,
            className: 'user-menu-dropdown-menu',
          }}
          placement="topRight"
        >
          <Button
            type="text"
            block={!collapsed}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: collapsed ? 0 : 8,
              justifyContent: collapsed ? 'center' : 'flex-end',
              paddingLeft: collapsed ? 0 : undefined,
            }}
          >
            <Avatar {...avatarProps} />

            {!collapsed && (
              <span
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  lineHeight: '1.5rem',
                }}
              >
                {displayName}
              </span>
            )}
          </Button>
        </Dropdown>
      </div>
      {profileModals}
    </>
  );
};

export default UserMenuFooter;
